import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceLists } from '@/api/getPriceList';
import { usePostPaymentSubscriptionSession } from '@/features/auth/api/postPaymentSubscriptionSession';
import { calculateDiscountPercentage } from '@/utils/price';
import type { Price } from '@/types';
import { AxiosError } from 'axios';

const findPriceByInterval = (
  prices: Price[] | undefined,
  interval: 'year' | 'month'
) => {
  return prices?.find((price) => price.recurringInterval === interval);
};

type UseSubscriptionParams = {
  campaignCode?: string | null;
  isoCountryCode?: string;
};

const getValidationErrorMessage = (error: unknown) => {
  if (!(error instanceof AxiosError)) return undefined;
  if (error.response?.status !== 422) return undefined;

  const message = error.response?.data?.message;
  if (typeof message !== 'string' || message.length === 0) return undefined;

  return message;
};

export const useSubscription = ({
  campaignCode,
  isoCountryCode,
}: UseSubscriptionParams) => {
  const [isYearly, setIsYearly] = useState(true);
  const [uiErrorMessage, setUiErrorMessage] = useState<string>();
  const { mutate, isPending: isMutating } = usePostPaymentSubscriptionSession();
  const { t } = useTranslation();

  const normalizedCampaignCode = campaignCode?.trim() || null;
  const { data: prices, error: priceListError } = usePriceLists({
    campaignCode: normalizedCampaignCode,
    isoCountryCode,
  });
  const yearlyPrice = findPriceByInterval(prices, 'year');
  const monthlyPrice = findPriceByInterval(prices, 'month');
  const currentPrice = isYearly ? yearlyPrice : monthlyPrice;

  const yearlyDiscountPercentage = calculateDiscountPercentage(
    monthlyPrice?.displayAmountMonthly || 0,
    yearlyPrice?.displayAmountMonthly || 0
  );

  useEffect(() => {
    if (!priceListError) {
      setUiErrorMessage(undefined);
      return;
    }

    const validationMessage = getValidationErrorMessage(priceListError);
    setUiErrorMessage(validationMessage);
  }, [priceListError]);

  const handleSubscribe = () => {
    if (!currentPrice) return;
    setUiErrorMessage(undefined);

    mutate(
      {
        priceId: currentPrice.id,
        campaignCode: normalizedCampaignCode,
        isoCountryCode,
      },
      {
        onSuccess: (data) => {
          window.location.href = data.url;
        },
        onError: (error) => {
          console.error(error);
          const validationMessage = getValidationErrorMessage(error);
          if (validationMessage) {
            setUiErrorMessage(validationMessage);
            return;
          }

          setUiErrorMessage(
            t(
              'プランの選択に失敗しました。何度試しても解決しない場合はサポートに連絡いただけると幸いです。'
            )
          );
        },
      }
    );
  };

  return {
    isYearly,
    setIsYearly,
    uiErrorMessage,
    currentPrice,
    yearlyDiscountPercentage,
    handleSubscribe,
    isMutating,
  };
};
